import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import BoxedTitle from "@/components/BoxedTitle";
import { clearCache } from "@/pages/admin/adminCache";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const KEY_STORAGE = "vbb_admin_key";

const SECTIONS = [
  {
    id: "company",
    label: "Company",
    questions: [
      { q: "Company name", note: "verybigbox / Very Big Box LLC" },
      { q: "Company URL", note: "https://verybigbox.com" },
      { q: "Describe what your company does in 50 characters or less.", note: "Candidate: \"Boutique web + app studio, shipped in 2 weeks.\" (44 chars)" },
      { q: "What is your company going to make? Please describe your product and what it does or will do.", note: "Angle: describe the change VBB creates — production-grade custom sites in weeks not months, with no vendor lock-in." },
      { q: "If you have a demo, what is the URL?", note: "verybigbox.com is itself the demo." },
      { q: "Please provide a video demo (max 1 min) of you or a cofounder using or demonstrating your product.", note: "Consider: screen-record building a component in Emergent + shipping it live in ~60s. Shows unfair advantage directly." },
    ],
  },
  {
    id: "founders",
    label: "Founders",
    questions: [
      { q: "Who are your founders? Tell us about each one.", note: "Bio, credentials, credibility for THIS company. If solo: address it head-on." },
      { q: "How long have the founders known one another and how did you meet?", note: "Skip if solo." },
      { q: "Which of the founders are, or plan to be, full-time on the project?", note: "Full-time = strongest signal. Part-time = state plan to go full-time and when." },
      { q: "What is your relevant experience?", note: "Prior startups, technical background, domain expertise. Reference specific shipped projects with results." },
      { q: "Please tell us about the time you most successfully hacked some (non-computer) system to your advantage.", note: "Signal they want: ingenuity, resourcefulness, willingness to bend rules. Story format, 1–2 paragraphs." },
    ],
  },
  {
    id: "idea",
    label: "Idea",
    questions: [
      { q: "Why did you pick this idea to work on? Do you have domain expertise? How do you know people need what you're making?", note: "Two-part: authentic origin story + hard evidence of demand." },
      { q: "What's new about what you're making? What substitutes do people resort to?", note: "Substitutes: WordPress templates (limited), Portland dev shop ($50K, 4mo), Wix/Squarespace (lock-in), offshore (quality risk). VBB = missing boutique tier." },
      { q: "Who are your competitors, and who might become competitors? Who do you fear most?", note: "Use /admin/market/competitors verbatim. Bigger threat framing: AI-native builder tools (Bolt, v0, Emergent consumer)." },
      { q: "What do you understand about your business that other companies just don't get?", note: "The 'insight' question — heavily weighted. VBB: shops price on hours, but AI collapses hours; handoff model is a moat where most shops build lock-in on purpose." },
      { q: "How do or will you make money? How much could you make?", note: "Reference /admin/market/pricing-memo. $18–40K/project × N. Reseller channel × partners × projects for year 2." },
      { q: "How will you get your first, or next, 100 customers?", note: "Local Yamhill/Lincoln County outbound, competitor-referral partnerships, geo SEO, delivered-project referrals." },
    ],
  },
  {
    id: "progress",
    label: "Progress",
    questions: [
      { q: "How far along are you? Do you have users, revenue, a beta, or an MVP?", note: "The live site itself + admin inbox submissions as demand evidence." },
      { q: "How long have you been working on this?", note: "Be honest about start date." },
      { q: "If any of the founders have worked on this idea previously, please tell us more about it.", note: "" },
    ],
  },
  {
    id: "ai-demo",
    label: "AI demo (2026 addition — VBB's strongest lever)",
    questions: [
      { q: "Upload a markdown file or transcript from Claude Code, Cursor, or another AI coding tool showing your recent work.", note: "Options: (a) Emergent chat history for verybigbox.com, (b) /app/memory/PRD.md — 14 iterations shipped live, (c) a single-feature build start to finish. Recommendation: PRD.md + a representative iteration transcript." },
    ],
  },
  {
    id: "logistics",
    label: "Logistics",
    questions: [
      { q: "How did you hear about Y Combinator?", note: "Honest answer." },
      { q: "Anything else we should know?", note: "Freeform — anything strong you couldn't fit above." },
    ],
  },
];

const KEY_MAP_LINKS = [
  { label: "Pricing memo (Q13, Q14, Q16)", to: "/admin/market" },
  { label: "Competitor detail pages (Q14)", to: "/admin/market/competitors" },
  { label: "Real submissions as demand evidence (Q20)", to: "/admin/inbox" },
];

export default function YCApplication() {
  const navigate = useNavigate();
  const [key, setKey] = useState(() => sessionStorage.getItem(KEY_STORAGE) || "");

  useEffect(() => {
    if (!key) navigate("/login?next=/admin/yc", { replace: true });
  }, [key, navigate]);

  const onLock = () => {
    sessionStorage.removeItem(KEY_STORAGE);
    clearCache();
    setKey("");
    navigate("/login?next=/admin/yc", { replace: true });
  };

  const onDownloadApplication = async () => {
    try {
      const resp = await fetch(`${API}/admin/yc-application`, {
        headers: { "X-Admin-Key": key },
      });
      if (!resp.ok) {
        alert(
          resp.status === 401
            ? "Session expired. Please sign in again."
            : `Download failed (${resp.status}).`
        );
        return;
      }
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "VBB_YC_Application_Draft.pdf";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      alert("Download failed. Check your connection.");
    }
  };

  if (!key) return null;

  return (
    <main
      className="mx-auto max-w-[1400px] px-6 lg:px-10 pt-20 lg:pt-28 pb-32"
      data-testid="admin-yc-page"
    >
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <div className="vbb-overline">
            <Link to="/admin" className="vbb-link" data-testid="crumb-admin">
              Admin
            </Link>{" "}
            / YC Application
          </div>
          <h1
            className="font-display uppercase text-5xl sm:text-6xl lg:text-7xl mt-6 tracking-tighter leading-[0.95] pl-[0.35em]"
            data-testid="admin-yc-title"
          >
            <BoxedTitle size="lg" thick>
              YC.
            </BoxedTitle>
          </h1>
          <p className="mt-3 max-w-xl text-[15px] leading-relaxed text-[#6b6b6b]">
            Question reference and prep notes for the Y Combinator
            application. Draft your actual answers wherever you like — this
            page is a checklist and idea bank.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Link
            to="/admin"
            className="vbb-btn vbb-btn-ghost"
            data-testid="admin-back-to-home"
          >
            ← Admin
          </Link>
          <button
            onClick={onDownloadApplication}
            className="vbb-btn"
            data-testid="yc-download-btn"
          >
            Download draft ↓
          </button>
          <button
            onClick={onLock}
            className="vbb-btn vbb-btn-ghost"
            data-testid="admin-lock-btn"
          >
            Lock
          </button>
        </div>
      </div>

      {/* Key deadlines callout */}
      <section
        className="mt-12 border-l-4 border-[#e34234] bg-white p-6 lg:p-8"
        data-testid="yc-deadlines"
      >
        <div className="vbb-overline text-[#e34234]">Deadlines</div>
        <p className="mt-3 text-[15px] leading-relaxed text-[#0a0a0a]">
          <strong>Spring 2026 batch</strong> — on-time deadline{" "}
          <strong>July 27, 8pm PT</strong>. Decisions <strong>August 28</strong>.
          Verify current at{" "}
          <a
            href="https://www.ycombinator.com/apply"
            target="_blank"
            rel="noopener noreferrer"
            className="vbb-link"
          >
            ycombinator.com/apply
          </a>
          .
        </p>
      </section>

      {/* Where existing VBB work maps to answers */}
      <section className="mt-10" data-testid="yc-existing-work">
        <div className="vbb-overline">Existing VBB work you can cite</div>
        <ul className="mt-3 space-y-2">
          {KEY_MAP_LINKS.map((item) => (
            <li key={item.label} className="text-[15px] leading-relaxed">
              —{" "}
              <Link to={item.to} className="vbb-link">
                {item.label}
              </Link>
            </li>
          ))}
          <li className="text-[15px] leading-relaxed">
            — <code className="font-mono-tiny">/app/memory/PRD.md</code> —
            14-iteration build log (perfect for the AI-demo upload, Q23)
          </li>
        </ul>
      </section>

      {/* Questions by section */}
      {SECTIONS.map((section) => (
        <section
          key={section.id}
          className="mt-14"
          data-testid={`yc-section-${section.id}`}
        >
          <h2 className="font-display uppercase text-3xl tracking-tight">
            {section.label}
          </h2>
          <ol className="mt-6 space-y-8">
            {section.questions.map((item) => (
              <li key={item.q} className="border-l-2 border-[#0a0a0a] pl-5">
                <p className="text-[16px] leading-relaxed text-[#0a0a0a]">
                  {item.q}
                </p>
                {item.note && (
                  <p className="mt-2 text-[14px] leading-relaxed text-[#6b6b6b] italic">
                    {item.note}
                  </p>
                )}
              </li>
            ))}
          </ol>
        </section>
      ))}

      {/* Story hypothesis */}
      <section
        className="mt-14 border border-[#0a0a0a] p-8 lg:p-10 bg-white"
        data-testid="yc-story-hypothesis"
      >
        <div className="vbb-overline">Working story hypothesis (edit freely)</div>
        <p className="mt-4 text-[16px] leading-relaxed text-[#2a2a2a]">
          Every boutique web shop in the country still prices in hours and
          lock-in. AI collapses both. We ship custom sites and apps in 2
          weeks, transfer them to the client&apos;s own infrastructure, and take
          no ongoing fees. We&apos;re testing a channel partnership model with
          existing WordPress shops so they can offer capabilities they can&apos;t
          build in-house. Early data:{" "}
          <em className="text-[#6b6b6b]">[submissions / signed deals / partner conversations]</em>.
        </p>
      </section>

      {/* Failure modes */}
      <section className="mt-10" data-testid="yc-failure-modes">
        <div className="vbb-overline text-[#e34234]">Failure modes to avoid</div>
        <ul className="mt-3 space-y-2 text-[15px] leading-relaxed text-[#2a2a2a]">
          <li>
            — Don&apos;t pitch &ldquo;we use AI.&rdquo; Every applicant will in 2026. Pitch{" "}
            <em>what changes because of it</em> (price, delivery, handoff).
          </li>
          <li>
            — Don&apos;t hide solo-founder status. Address it and explain why it
            works for now.
          </li>
          <li>
            — Don&apos;t undersell verybigbox.com itself. It <em>is</em> the demo.
            Live production site + admin tooling + real submissions beats
            most YC applicants&apos; evidence.
          </li>
        </ul>
      </section>
    </main>
  );
}
